"use client";

import { useState, useEffect } from "react";
import {
  BarChart3,
  TrendingUp,
  Users,
  Heart,
  Download,
  Activity,
  Target,
} from "lucide-react";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Area,
  ComposedChart,
  Line as RechartsLine,
  Legend,
} from "recharts";
import {
  Box,
  Grid,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  CircularProgress,
  createTheme,
  ThemeProvider,
  CssBaseline,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";
import apiEndpoints from "../../apiconfig";
import * as XLSX from "xlsx"; // Import the xlsx library

// Create a theme with Montserrat font
const montserratTheme = createTheme({
  typography: {
    fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    body1: {
      fontWeight: 400,
    },
    body2: {
      fontWeight: 500,
    },
    caption: {
      fontWeight: 400,
    },
    button: {
      fontWeight: 500,
      textTransform: "none",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
          borderRadius: 8,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
        },
      },
    },
  },
});

export default function Reports() {
  const [activeTab, setActiveTab] = useState("user-growth");
  const [selectedTimeRange, setSelectedTimeRange] = useState("7days");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isLaptop = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const COLORS = ["#3b82f6", "#8b5cf6", "#f59e0b", "#6b7280", "#10b981"];
  const SUBSCRIPTION_COLORS = {
    basic: "#3b82f6",
    premium: "#8b5cf6",
  };

  useEffect(() => {
    fetchData();
  }, [selectedTimeRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${apiEndpoints.reports}?timeRange=${selectedTimeRange}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => new Intl.NumberFormat("en-IN").format(num);

  const handleExportReport = () => {
    if (!data) return;

    const {
      userGrowthData = [],
      subscriptionSalesData = [],
      activeUsersDistribution = [],
      keyMetrics = {},
    } = data;

    // Prepare data for Excel export
    const workbook = XLSX.utils.book_new();

    // 1. Key Metrics Sheet
    const keyMetricsData = [
      ["Metric", "Value"],
      ["Total Users", keyMetrics.totalUsers],
      ["Active Users", keyMetrics.activeUsers],
      ["Premium Users", keyMetrics.premiumUsers],
      ["Free Users", keyMetrics.freeUsers],
      ["Monthly Growth Rate", `${keyMetrics.monthlyGrowthRate}%`],
      ["Conversion Rate", `${keyMetrics.conversionRate}%`],
      ["Churn Rate", `${keyMetrics.churnRate}%`],
    ];
    const keyMetricsSheet = XLSX.utils.aoa_to_sheet(keyMetricsData);
    XLSX.utils.book_append_sheet(workbook, keyMetricsSheet, "Key Metrics");

    // 2. User Growth Data Sheet
    const userGrowthHeaders = [
      "Period",
      "Total Users",
      "New Users",
      "Active Users",
    ];
    const userGrowthRows = userGrowthData.map((item) => [
      item.period,
      item.totalUsers,
      item.newUsers,
      item.activeUsers,
    ]);
    userGrowthRows.unshift(userGrowthHeaders);
    const userGrowthSheet = XLSX.utils.aoa_to_sheet(userGrowthRows);
    XLSX.utils.book_append_sheet(workbook, userGrowthSheet, "User Growth");

    // 3. Subscription Sales Data Sheet
    const subscriptionHeaders = [
      "Period",
      "Basic Plan",
      "Premium Plan",
      "Total",
    ];
    const subscriptionRows = subscriptionSalesData.map((item) => [
      item.period,
      item.basic || 0,
      item.premium || 0,
      (item.basic || 0) + (item.premium || 0),
    ]);
    subscriptionRows.unshift(subscriptionHeaders);
    const subscriptionSheet = XLSX.utils.aoa_to_sheet(subscriptionRows);
    XLSX.utils.book_append_sheet(
      workbook,
      subscriptionSheet,
      "Subscription Sales",
    );

    // 4. Active Users Distribution Sheet
    const distributionHeaders = ["Category", "Users", "Percentage"];
    const totalUsersDist = activeUsersDistribution.reduce(
      (sum, curr) => sum + curr.value,
      0,
    );
    const distributionRows = activeUsersDistribution.map((item) => [
      item.name,
      item.value,
      totalUsersDist > 0
        ? `${((item.value / totalUsersDist) * 100).toFixed(1)}%`
        : "0%",
    ]);
    distributionRows.unshift(distributionHeaders);
    const distributionSheet = XLSX.utils.aoa_to_sheet(distributionRows);
    XLSX.utils.book_append_sheet(
      workbook,
      distributionSheet,
      "User Distribution",
    );

    // Generate Excel file and trigger download
    const fileName = `Matrimony_Reports_${selectedTimeRange}_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <Typography color="error">Error loading data: {error}</Typography>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <Typography>No data available</Typography>
      </Box>
    );
  }

  const {
    userGrowthData,
    subscriptionSalesData,
    activeUsersDistribution,
    keyMetrics,
  } = data;

  // Calculate total subscription sales for each plan type
  const subscriptionTotals =
    subscriptionSalesData && subscriptionSalesData.length > 0
      ? {
          basic: subscriptionSalesData.reduce(
            (sum, item) => sum + (item.basic || 0),
            0,
          ),
          premium: subscriptionSalesData.reduce(
            (sum, item) => sum + (item.premium || 0),
            0,
          ),
        }
      : { basic: 0, premium: 0 };

  const totalSubscriptions =
    subscriptionTotals.basic + subscriptionTotals.premium;

  return (
    <ThemeProvider theme={montserratTheme}>
      <CssBaseline />
      <Box
        sx={{ width: "100%", pb: 4, fontFamily: '"Montserrat", sans-serif' }}
        display="flex"
        flexDirection="column"
        gap={4}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight={700} fontFamily="inherit">
              Reports & Analytics
            </Typography>
            <Typography
              variant={isMobile ? "body2" : "body1"}
              color="text.secondary"
              fontFamily="inherit"
            >
              Comprehensive insights and analytics for your matrimony platform
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: isMobile ? 120 : 140 }}>
              <InputLabel id="time-range-label" sx={{ fontFamily: "inherit" }}>
                Time Range
              </InputLabel>
              <Select
                labelId="time-range-label"
                value={selectedTimeRange}
                label="Time Range"
                onChange={(e) => setSelectedTimeRange(e.target.value)}
                sx={{ fontFamily: "inherit" }}
              >
                <MenuItem value="7days" sx={{ fontFamily: "inherit" }}>
                  Last 7 days
                </MenuItem>
                <MenuItem value="30days" sx={{ fontFamily: "inherit" }}>
                  Last 30 days
                </MenuItem>
                <MenuItem value="90days" sx={{ fontFamily: "inherit" }}>
                  Last 90 days
                </MenuItem>
                <MenuItem value="1year" sx={{ fontFamily: "inherit" }}>
                  Last year
                </MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              startIcon={!isMobile && <Download />}
              onClick={handleExportReport}
              sx={{
                borderColor: "#ffe599",
                backgroundColor: "#ffe599",
                color: "#000000ff",
                borderRadius: 2,
                fontFamily: "inherit",
              }}
            >
              {isMobile ? "Export" : "Export Report"}
            </Button>
          </Box>
        </Box>

        {/* Key Metrics */}
        <Grid
          container
          spacing={2}
          width={"100%"}
          justifyContent="space-between"
        >
          <Grid
            item
            sx={{ width: { xs: "100%", sm: "48%", md: "23%", lg: "22%" } }}
          >
            <Card sx={{ borderLeft: "4px solid #3b82f6", borderRadius: 4 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontFamily="inherit"
                    >
                      Total Users
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      fontFamily="inherit"
                    >
                      {formatNumber(keyMetrics.totalUsers)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      fontFamily="inherit"
                    >
                      <TrendingUp fontSize={14} />+
                      {keyMetrics.monthlyGrowthRate}% this month
                    </Typography>
                  </Box>
                  <Users fontSize={32} color="#3b82f6" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            width={
              isMobile
                ? "100%"
                : isTablet
                  ? "48.5%"
                  : isLaptop
                    ? "23.5%"
                    : "24%"
            }
          >
            <Card sx={{ borderLeft: "4px solid #10b981", borderRadius: 4 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontFamily="inherit"
                    >
                      Active Users
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      fontFamily="inherit"
                    >
                      {formatNumber(keyMetrics.activeUsers)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontFamily="inherit"
                    >
                      {(
                        (keyMetrics.activeUsers / keyMetrics.totalUsers) *
                        100
                      ).toFixed(1)}
                      % of total users
                    </Typography>
                  </Box>
                  <Activity fontSize={32} color="#10b981" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            width={
              isMobile
                ? "100%"
                : isTablet
                  ? "48.5%"
                  : isLaptop
                    ? "23.5%"
                    : "24%"
            }
          >
            <Card sx={{ borderLeft: "4px solid #8b5cf6", borderRadius: 4 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontFamily="inherit"
                    >
                      Premium Users
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      fontFamily="inherit"
                    >
                      {formatNumber(keyMetrics.premiumUsers)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="#8b5cf6"
                      fontFamily="inherit"
                    >
                      {keyMetrics.conversionRate}% conversion rate
                    </Typography>
                  </Box>
                  <Heart fontSize={32} color="#8b5cf6" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            width={
              isMobile
                ? "100%"
                : isTablet
                  ? "48.5%"
                  : isLaptop
                    ? "23.5%"
                    : "24%"
            }
          >
            <Card sx={{ borderLeft: "4px solid #facc15", borderRadius: 4 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontFamily="inherit"
                    >
                      Free Users
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      fontFamily="inherit"
                    >
                      {formatNumber(keyMetrics.freeUsers)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="#facc15"
                      fontFamily="inherit"
                    >
                      {keyMetrics.churnRate}% churn rate
                    </Typography>
                  </Box>
                  <Target fontSize={32} color="#facc15" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ borderRadius: "16px", overflow: "hidden" }}>
          <Box sx={{ borderBottom: 1, borderColor: "#eee", px: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons={false}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  minHeight: 48,
                  color: "text.secondary",
                },
                "& .Mui-selected": {
                  color: "#b8860b !important",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#b8860b",
                  height: "3px",
                  borderRadius: "3px 3px 0 0",
                },
              }}
            >
              <Tab value="user-growth" label="User Growth" />
              <Tab value="subscription-sales" label="Subscription Sales" />
              <Tab value="active-users" label="Active Users" />
            </Tabs>
          </Box>
          <Box sx={{ p: 3 }}>
            {activeTab === "user-growth" && (
              <Box sx={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={userGrowthData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip
                      formatter={(value, name) => [formatNumber(value), name]}
                    />
                    <Area
                      type="monotone"
                      dataKey="totalUsers"
                      stroke="#3b82f6"
                      fill="#3b82f6"
                      fillOpacity={0.1}
                    />
                    <Bar dataKey="newUsers" fill="#10b981" />
                    <RechartsLine
                      type="monotone"
                      dataKey="activeUsers"
                      stroke="#8b5cf6"
                      strokeWidth={3}
                    />
                    <Legend />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            )}

            {activeTab === "subscription-sales" && (
              <Grid container spacing={3} width={"100%"}>
                <Grid
                  item
                  width={{ xs: "100%", sm: "48%" }}
                  sx={{ height: 320 }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={subscriptionSalesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip formatter={(value, name) => [value, name]} />
                      <Legend />
                      <Bar
                        dataKey="basic"
                        stackId="a"
                        fill={SUBSCRIPTION_COLORS.basic}
                        name="Basic Plan"
                      />
                      <Bar
                        dataKey="premium"
                        stackId="a"
                        fill={SUBSCRIPTION_COLORS.premium}
                        name="Premium Plan"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Grid>
                <Grid item width={{ xs: "100%", sm: "48%" }}>
                  <Typography variant="h6" gutterBottom fontFamily="inherit">
                    Subscription Summary
                  </Typography>
                  {[
                    {
                      name: "Basic Plan",
                      value: subscriptionTotals.basic,
                      color: SUBSCRIPTION_COLORS.basic,
                    },
                    {
                      name: "Premium Plan",
                      value: subscriptionTotals.premium,
                      color: SUBSCRIPTION_COLORS.premium,
                    },
                  ].map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            backgroundColor: item.color,
                          }}
                        />
                        <Typography variant="body2" fontFamily="inherit">
                          {item.name}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          fontFamily="inherit"
                        >
                          {formatNumber(item.value)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontFamily="inherit"
                        >
                          {totalSubscriptions > 0
                            ? ((item.value / totalSubscriptions) * 100).toFixed(
                                1,
                              )
                            : 0}
                          %
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e0e0e0" }}>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        fontFamily="inherit"
                      >
                        Total Subscriptions
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        fontFamily="inherit"
                      >
                        {formatNumber(totalSubscriptions)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}

            {activeTab === "active-users" && (
              <Box sx={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={activeUsersDistribution}
                      cx={isMobile ? "50%" : "50%"}
                      cy="50%"
                      labelLine={!isMobile}
                      label={({ name, percent }) =>
                        isMobile
                          ? `${(percent * 100).toFixed(0)}%`
                          : `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={isMobile ? 60 : 80}
                      dataKey="value"
                    >
                      {activeUsersDistribution.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.color || COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Legend
                      verticalAlign={isMobile ? "bottom" : "middle"}
                      align={isMobile ? "center" : "right"}
                      layout={isMobile ? "horizontal" : "vertical"}
                    />
                    <Tooltip
                      formatter={(value) => [formatNumber(value), "Users"]}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </ThemeProvider>
  );
}
